import { z } from 'zod';
import { apiClient } from '../client';

/**
 * Customer endpoints — B2B clients of a tenant (ADR-024). All routes are
 * scoped to the caller's active Organisation; the backend enforces the
 * scoping. This frontend module is just a typed wrapper.
 */

const CustomerSchema = z.object({
  id: z.string(),
  parent_organisation_id: z.string(),
  company_name: z.string(),
  contact_name: z.string().default(''),
  email: z.string().default(''),
  phone: z.string().default(''),
  industry: z.string().default(''),
  is_active: z.boolean(),
  // ADR-024 phase 2.c — CRM (booleans + non-secret URLs only).
  calendly_url: z.string().default(''),
  google_calendar_enabled: z.boolean().default(false),
  google_calendar_calendar_id: z.string().default(''),
  google_calendar_account_email: z.string().default(''),
  hubspot_connected: z.boolean().default(false),
  swoft_crm_enabled: z.boolean().default(false),
  swoft_crm_url: z.string().default(''),
  swoft_crm_tenant_id: z.string().default(''),
  auto_send_call_activity: z.boolean().default(false),
  active_crm_provider: z.string().default('none'),
  // ADR-031 — coaching personalisation per Customer (overrides tenant-wide
  // prompt at runtime via CoachingResolver merge).
  coaching_system_prompt: z.string().default(''),
  coaching_context_docs: z.string().default(''),
  created_date: z.coerce.date(),
  updated_date: z.coerce.date(),
});

export type Customer = z.infer<typeof CustomerSchema>;

const ListEnvelope = z.object({ customers: z.array(z.unknown()) });

export async function listCustomers(opts?: { activeOnly?: boolean; signal?: AbortSignal }): Promise<Customer[]> {
  const data = await apiClient.get<unknown>('/customers', {
    query: opts?.activeOnly === false ? { activeOnly: 'false' } : undefined,
    signal: opts?.signal,
  });
  const env = ListEnvelope.parse(data);
  return env.customers.map((c) => CustomerSchema.parse(c));
}

export interface CustomerInput {
  company_name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  industry?: string;
  calendly_url?: string;
  // ADR-031 — per-Customer CRM config (writable inline; OAuth fields stay
  // server-side and only mutate via dedicated /customers/:id/{hubspot,google}
  // endpoints).
  swoft_crm_enabled?: boolean;
  swoft_crm_url?: string;
  swoft_crm_api_key?: string;
  swoft_crm_tenant_id?: string;
  // ADR-036: OAuth client id and inbound events secret (write-only).
  swoft_client_id?: string;
  swoft_events_secret?: string;
  // Opt-in: push every qualified call to the CRM without the caller's button.
  auto_send_call_activity?: boolean;
  active_crm_provider?: 'none' | 'hubspot' | 'swoft';
  coaching_system_prompt?: string;
  coaching_context_docs?: string;
}

export async function createCustomer(input: CustomerInput): Promise<Customer> {
  const raw = await apiClient.post<unknown>('/customers', input);
  return CustomerSchema.parse(raw);
}

export async function updateCustomer(id: string, input: Partial<CustomerInput>): Promise<Customer> {
  const raw = await apiClient.patch<unknown>(`/customers/${id}`, input);
  return CustomerSchema.parse(raw);
}

export async function archiveCustomer(id: string): Promise<Customer> {
  const raw = await apiClient.post<unknown>(`/customers/${id}/archive`, {});
  return CustomerSchema.parse(raw);
}

export async function reactivateCustomer(id: string): Promise<Customer> {
  const raw = await apiClient.post<unknown>(`/customers/${id}/reactivate`, {});
  return CustomerSchema.parse(raw);
}

const MigrationResultSchema = z.object({
  customer: z.unknown(),
  migrated: z.object({
    prospects: z.number().int().nonnegative(),
    calls: z.number().int().nonnegative(),
    meetings: z.number().int().nonnegative(),
  }),
});

export interface MigrationResult {
  customer: Customer;
  migrated: { prospects: number; calls: number; meetings: number };
}

export async function migrateFromTenant(): Promise<MigrationResult> {
  const raw = await apiClient.post<unknown>('/customers/migrate-from-tenant', {});
  const parsed = MigrationResultSchema.parse(raw);
  return {
    customer: CustomerSchema.parse(parsed.customer),
    migrated: parsed.migrated,
  };
}

const BackfillResultSchema = z.object({
  ok: z.boolean(),
  organisationCount: z.number().int().nonnegative(),
  backfilled: z.record(z.number().int().nonnegative()),
});

export type BackfillResult = z.infer<typeof BackfillResultSchema>;

/** ADR-025 — super_admin only. Walks the 4 main tenant-scoped collections
 *  and backfills organisation_id from client_email. Idempotent. */
export async function backfillOrganisationId(): Promise<BackfillResult> {
  const raw = await apiClient.post<unknown>('/admin/backfill-organisation-id', {});
  return BackfillResultSchema.parse(raw);
}

// ─── Per-Customer OAuth (ADR-024 phase 2.c) ─────────────────────────────

/** Returns the URL to redirect the browser to so the admin starts the
 *  HubSpot consent flow for a specific Customer. */
export function customerHubspotAuthorizeUrl(customerId: string): string {
  // We hit the API directly with a redirect, so we just return the URL.
  // The browser navigates here; the backend issues a 302 to HubSpot.
  return `/api/customers/${encodeURIComponent(customerId)}/hubspot/oauth/authorize`;
}

export async function disconnectCustomerHubspot(customerId: string): Promise<void> {
  await apiClient.post(`/customers/${customerId}/hubspot/oauth/disconnect`, {});
}

export function customerGoogleAuthorizeUrl(customerId: string): string {
  return `/api/customers/${encodeURIComponent(customerId)}/google/oauth/authorize`;
}

export async function disconnectCustomerGoogle(customerId: string): Promise<void> {
  await apiClient.post(`/customers/${customerId}/google/oauth/disconnect`, {});
}
